import React, { useState } from 'react'

import { AccountBalanceWalletOutlined, AttachMoneyOutlined, InfoOutlined, MoreTimeOutlined } from '@mui/icons-material'
import { Paper, Skeleton, Stack, Typography } from '@mui/material'

import { PaymentCredits } from '@/types/Payment'
import { ChargingPoint } from '@/types/ChargingPoint'
import { numericUtils } from '@/utils/numeric'

import { TariffDetailsModal } from './TariffDetailsModal'

interface ChargingPointInfoHeaderProps {
  paymentCredits: PaymentCredits | undefined
  chargingPoint: ChargingPoint | undefined
  isLoading?: boolean
}

export const ChargingPointInfoHeader: React.FC<ChargingPointInfoHeaderProps> = ({
  paymentCredits,
  chargingPoint,
  isLoading = false,
}) => {
  const [isTariffModalOpen, setIsTariffModalOpen] = useState(false)

  const openTariffModal = () => setIsTariffModalOpen(true)
  const closeTariffModal = () => setIsTariffModalOpen(false)

  const activationFee = chargingPoint?.current_activation_fee ?? chargingPoint?.activation_fee ?? 0

  const infoItems = [
    {
      icon: <AccountBalanceWalletOutlined sx={{ fontSize: 20 }} color="primary" />,
      label: 'Saldo',
      value: numericUtils.formatNumberToBRLCurrency(
        paymentCredits?.balance ? Number(paymentCredits?.balance) : undefined
      ),
      onClick: undefined as (() => void) | undefined,
      caption: undefined as string | undefined,
    },
    {
      icon: <AttachMoneyOutlined sx={{ fontSize: 20 }} color="primary" />,
      label: 'Valor agora',
      value: numericUtils.formatNumberToBRLCurrency(chargingPoint?.current_rate_per_kwh ?? chargingPoint?.rate_per_kwh),
      onClick: chargingPoint ? openTariffModal : undefined,
      caption: activationFee > 0 ? `+ ${numericUtils.formatNumberToBRLCurrency(activationFee)} de ativação` : undefined,
    },
    {
      icon: <MoreTimeOutlined sx={{ fontSize: 20 }} color="primary" />,
      label: 'Ociosidade',
      value: `${numericUtils.formatNumberToBRLCurrency(chargingPoint?.current_idle_rate ?? chargingPoint?.idle_rate)}/min`,
      onClick: undefined as (() => void) | undefined,
      caption: undefined as string | undefined,
    },
  ]

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          padding: 1.5,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          width: '100%',
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          {Array.from({ length: 3 }).map((_, index) => (
            <Stack key={index} direction="column" alignItems="center" spacing={0.25} flex={1} minWidth={0}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width={40} height={12} />
              <Skeleton variant="text" width={50} height={16} />
            </Stack>
          ))}
        </Stack>
      </Paper>
    )
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          padding: 1.5,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          width: '100%',
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          {infoItems.map((item, index) => (
            <Stack
              key={index}
              direction="column"
              alignItems="center"
              spacing={0.25}
              flex={1}
              minWidth={0}
              onClick={item.onClick}
              role={item.onClick ? 'button' : undefined}
              sx={item.onClick ? { cursor: 'pointer' } : undefined}
            >
              {item.icon}
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <Typography variant="caption" color="text.secondary" textAlign="center" fontSize={11} lineHeight={1.2}>
                  {item.label}
                </Typography>
                {item.onClick && <InfoOutlined sx={{ fontSize: 12 }} color="primary" />}
              </Stack>
              <Typography
                variant="caption"
                fontWeight="bold"
                textAlign="center"
                fontSize={12}
                lineHeight={1.2}
                noWrap
                sx={{ maxWidth: '100%' }}
              >
                {item.value}
              </Typography>
              {item.caption && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  fontSize={10}
                  lineHeight={1.2}
                  noWrap
                  sx={{ maxWidth: '100%' }}
                >
                  {item.caption}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      </Paper>

      {chargingPoint && (
        <TariffDetailsModal open={isTariffModalOpen} onClose={closeTariffModal} chargingPoint={chargingPoint} />
      )}
    </>
  )
}
